// @oasis-permission-inherits: /organizations (orgs:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { LookupFormUI } from "~/modules/lookup/components/lookup-form-ui";
import { RecentSearches } from "~/modules/lookup/components/recent-searches";
import { useSavedSearches } from "~/modules/lookup/hooks/use-saved-searches";
import { CreateEnterpriseDialog } from "~/modules/org/components/create-enterprise-dialog";

const querySchema = z.object({
  sheet: z.enum(["create"]).optional(),
});

export const Route = createFileRoute("/_dash/organizations/")({
  component: RouteComponent,
  validateSearch: querySchema,
});

function RouteComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const [searchValue, setSearchValue] = useState("");
  const { getSearchesByType } = useSavedSearches();

  const orgSearches = getSearchesByType("org");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate({ to: "/organizations/view/$orgId", params: { orgId: searchValue.trim() } });
    }
  };

  const handleClear = () => {
    setSearchValue("");
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    if (pastedText) {
      navigate({ to: "/organizations/view/$orgId", params: { orgId: pastedText } });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex">
        <div className="flex flex-1">
          <h1 className="text-xl font-bold">Organizations</h1>
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <Icon name="options" className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Link to="/organizations" search={{ sheet: "create" }}>
                <DropdownMenuItem>
                  <Icon name="person" className="h-4 w-4 mr-2" />
                  Create a new Enterprise organization from emails
                </DropdownMenuItem>
              </Link>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <LookupFormUI
        placeholder="Enter organization ID..."
        searchValue={searchValue}
        isPending={false}
        hasActiveSearch={false}
        onSearchValueChange={setSearchValue}
        onSubmit={handleSearch}
        onClear={handleClear}
        onPaste={handlePaste}
      />

      <CreateEnterpriseDialog
        open={search.sheet === "create"}
        onClose={() => navigate({ search: (old) => ({ ...old, sheet: undefined }) })}
      />

      <RecentSearches searches={orgSearches} type="org" />
    </div>
  );
}
